"use client";

import {
  Bell,
  CreditCard,
  Crown,
  Heart,
  Home,
  MapPin,
  PackageCheck,
  Shield,
  Smartphone,
  UserRound
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { cn } from "@/lib/utils";

const links = [
  { href: "/dashboard/profile", label: "Profile", icon: UserRound },
  { href: "/dashboard/orders", label: "Orders", icon: PackageCheck },
  { href: "/dashboard/listings", label: "My Listings", icon: Smartphone },
  { href: "/dashboard/wishlist", label: "Wishlist", icon: Heart },
  { href: "/dashboard/addresses", label: "Addresses", icon: MapPin },
  { href: "/dashboard/payments", label: "Payment methods", icon: CreditCard },
  { href: "/dashboard/notifications", label: "Notifications", icon: Bell },
  { href: "/dashboard/security", label: "Security", icon: Shield },
  { href: "/dashboard/prime", label: "Prime", icon: Crown },
  { href: "/", label: "Storefront", icon: Home }
];

export function DashboardShell({ children, title }: { children: React.ReactNode; title: string }) {
  const pathname = usePathname();

  return (
    <div className="mx-auto grid max-w-amazon gap-4 sm:gap-6 px-3 py-4 sm:py-8 sm:px-4 lg:grid-cols-[240px_1fr]">
      <aside className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-xs dark:border-white/10 dark:bg-slate-900 h-fit">
        <div className="px-1 py-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#FF8A00]">Your Account</p>
          <h1 className="text-lg sm:text-xl font-bold text-[#073B70] dark:text-white">{title}</h1>
        </div>
        <nav className="mt-2 flex flex-row gap-1.5 overflow-x-auto lg:flex-col lg:overflow-visible no-scrollbar touch-pan-x py-1">
          {links.map((link) => {
            const Icon = link.icon;
            const active = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "flex shrink-0 items-center gap-2 rounded-xl px-3.5 py-2 text-xs sm:text-sm font-bold transition-all min-h-[40px] touch-manipulation",
                  active
                    ? "bg-gradient-to-r from-[#087DB5] to-[#073B70] text-white shadow-xs"
                    : "text-slate-700 hover:bg-slate-100 bg-slate-50 lg:bg-transparent dark:text-slate-200 dark:bg-white/5 dark:hover:bg-white/10"
                )}
              >
                <Icon className={cn("h-4 w-4", active ? "text-white" : "text-[#087DB5]")} />
                <span>{link.label}</span>
              </Link>
            );
          })}
        </nav>
      </aside>
      <section className="min-w-0">{children}</section>
    </div>
  );
}
